import { useCallback, useMemo, useRef, useState } from 'react';
import { useApiToken } from '@/auth/hooks';
import { useCanAccess } from '@/auth/useCanAccess';
import {
  useStewardStore,
  type DraftEntity,
  type DraftRelationship,
} from './useStewardStore';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  FileText,
  Loader2,
  ArrowRight,
  AlertTriangle,
  User,
  MessageSquare,
  Calendar,
  ChevronDown,
  ChevronRight,
  Network,
  Boxes,
  Sparkles,
  ImageIcon,
} from 'lucide-react';
import type { Components } from 'react-markdown';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// ── Status badge colours ──────────────────────────────────────

const statusColours: Record<string, string> = {
  DRAFT: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  APPROVED: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  REJECTED: 'bg-red-500/15 text-red-400 border-red-500/30',
  PENDING_REVIEW: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  FINALIZED: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  OUTDATED: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  CREATED: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
};

const statusAccent: Record<string, string> = {
  PENDING_REVIEW: 'from-blue-500/80 to-indigo-500/80',
  FINALIZED: 'from-emerald-500/80 to-teal-500/80',
  OUTDATED: 'from-zinc-500/80 to-zinc-600/80',
  DRAFT: 'from-amber-500/80 to-orange-500/80',
};

export function StatusBadge({ status }: { status: string }) {
  const labels: Record<string, string> = {
    DRAFT: 'Entwurf',
    APPROVED: 'Freigegeben',
    REJECTED: 'Abgelehnt',
    PENDING_REVIEW: 'Zu prüfen',
    FINALIZED: 'Finalisiert',
    OUTDATED: 'Veraltet',
    CREATED: 'Erstellt',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${statusColours[status] ?? 'bg-muted text-muted-foreground border-border'}`}
    >
      {labels[status] ?? status}
    </span>
  );
}

// ── Type badge ────────────────────────────────────────────────

const typeConfig: Record<string, { icon: string; color: string }> = {
  Entity: { icon: '◆', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
  Process: { icon: '▶', color: 'text-orange-400 bg-orange-500/10 border-orange-500/20' },
  Rule: { icon: '■', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
  Source: { icon: '●', color: 'text-green-400 bg-green-500/10 border-green-500/20' },
};

export function TypeBadge({ type }: { type?: string }) {
  if (!type) return null;
  const cfg = typeConfig[type] ?? { icon: '○', color: 'text-muted-foreground bg-muted/50 border-border' };
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${cfg.color}`}>
      <span className="text-[8px]">{cfg.icon}</span> {type.replace('Meta', '')}
    </span>
  );
}

// ── Ontology classification badge ─────────────────────────────

export function OntologyBadge({ classification }: { classification?: string }) {
  if (!classification) return null;
  const isCore = classification === 'core_ontology';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${
        isCore
          ? 'border-violet-500/30 bg-violet-500/15 text-violet-400'
          : 'border-sky-500/30 bg-sky-500/15 text-sky-400'
      }`}
    >
      {isCore ? '◈ Kern-Ontologie' : '◇ Domain-Erweiterung'}
    </span>
  );
}

// ── Entity card with colored border accent ────────────────────

const entityBorderColor: Record<string, string> = {
  DRAFT: 'border-l-amber-500',
  APPROVED: 'border-l-emerald-500',
  REJECTED: 'border-l-red-500',
};

export function EntityCard({
  entity,
  citations,
  onApprove,
  onReject,
  canReview = true,
}: {
  entity: DraftEntity;
  citations?: Record<string, string>;
  onApprove: () => void;
  onReject: () => void;
  // DevPlan 0139/T2: Freigeben/Ablehnen nur für WS-Reviewer (Defense-in-Depth).
  canReview?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border border-border/60 border-l-[3px] ${entityBorderColor[entity.status] ?? 'border-l-border'} bg-card/50 backdrop-blur-sm p-3.5 space-y-2.5 transition-colors hover:bg-card/80`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-foreground">{entity.name}</span>
        <TypeBadge type={entity.type} />
        <OntologyBadge classification={entity.ontologyClassification} />
        <div className="ml-auto">
          <StatusBadge status={entity.status} />
        </div>
      </div>
      {entity.description && (
        <p className="text-[13px] leading-relaxed text-muted-foreground pl-0.5">
          {citations
            ? renderTextWithCitations(entity.description, citations)
            : entity.description}
        </p>
      )}
      {entity.status === 'DRAFT' && canReview && (
        <div className="flex items-center gap-2 pt-0.5">
          <Button
            size="sm"
            className="h-7 text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white"
            onClick={onApprove}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Freigeben
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1.5 text-red-400 hover:text-red-300 border-red-500/30 hover:border-red-500/50 hover:bg-red-500/10"
            onClick={onReject}
          >
            <XCircle className="h-3.5 w-3.5" />
            Ablehnen
          </Button>
        </div>
      )}
      {entity.status === 'DRAFT' && !canReview && (
        <p className="text-[11px] text-muted-foreground/70 pt-0.5">
          Freigabe erfordert die Workspace-Rolle „Reviewer".
        </p>
      )}
    </div>
  );
}

// ── Relationship card with flow visualization ─────────────────

export function RelationshipCard({ rel }: { rel: DraftRelationship }) {
  return (
    <div className="group flex items-center gap-0 rounded-lg border border-border/40 bg-card/30 backdrop-blur-sm px-0 py-0 text-xs transition-colors hover:bg-card/60 overflow-hidden">
      {/* Source node */}
      <div className="flex items-center gap-1.5 px-3 py-2.5 bg-blue-500/5 border-r border-border/30">
        <div className="h-2 w-2 rounded-full bg-blue-400/70" />
        <span className="font-medium text-foreground whitespace-nowrap">{rel.source}</span>
      </div>

      {/* Edge */}
      <div className="flex items-center gap-1.5 px-3 py-2.5 flex-shrink-0">
        <div className="h-px w-3 bg-muted-foreground/30" />
        <span className="rounded-full bg-muted/60 px-2 py-0.5 font-mono text-[10px] text-primary/80 border border-border/30 whitespace-nowrap">
          {rel.type}
        </span>
        <ArrowRight className="h-3 w-3 text-muted-foreground/60 shrink-0" />
      </div>

      {/* Target node */}
      <div className="flex items-center gap-1.5 px-3 py-2.5 bg-emerald-500/5 border-l border-border/30">
        <div className="h-2 w-2 rounded-full bg-emerald-400/70" />
        <span className="font-medium text-foreground whitespace-nowrap">{rel.target}</span>
      </div>

      {/* Description */}
      {rel.description && (
        <div className="ml-auto px-3 py-2.5 text-muted-foreground/60 group-hover:text-muted-foreground truncate max-w-[220px] border-l border-border/20 italic">
          {rel.description}
        </div>
      )}
    </div>
  );
}

// ── Stats mini cards ──────────────────────────────────────────

function StatCard({
  label,
  value,
  color,
  icon: Icon,
  onClick,
}: {
  label: string;
  value: number;
  color: string;
  icon: React.ElementType;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2.5 rounded-lg border border-border/40 bg-card/40 backdrop-blur-sm px-3.5 py-2.5 min-w-0 text-left transition-colors hover:bg-card/70 hover:border-border/60 cursor-pointer`}
    >
      <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-lg font-bold leading-none text-foreground">{value}</p>
        <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{label}</p>
      </div>
    </button>
  );
}

// ── Citation badge with hover tooltip ─────────────────────────

function CitationBadge({
  citeNumber,
  citations,
}: {
  citeNumber: string;
  citations: Record<string, string>;
}) {
  const text = citations[citeNumber];
  if (!text) {
    return (
      <sup className="inline-flex items-center justify-center ml-0.5 h-4 min-w-4 rounded-full bg-muted/50 px-1 text-[9px] font-semibold text-muted-foreground/60 leading-none cursor-default">
        {citeNumber}
      </sup>
    );
  }

  // Split "Experte: ..." / "Interviewer: ..." for nicer display
  const isExpert = text.startsWith('Experte:');
  const roleLabel = isExpert ? 'Experte' : text.startsWith('Interviewer:') ? 'Interviewer' : null;
  const content = roleLabel ? text.slice(roleLabel.length + 1).trim() : text;

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <sup className="inline-flex items-center justify-center ml-0.5 h-4 min-w-4 rounded-full bg-primary/15 px-1 text-[9px] font-bold text-primary leading-none cursor-help hover:bg-primary/25 transition-colors border border-primary/20">
          {citeNumber}
        </sup>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        className="max-w-sm p-0 overflow-hidden"
      >
        <div className="border-b border-border/60 bg-muted/30 px-3 py-1.5 flex items-center gap-2">
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
            Quelle #{citeNumber}
          </span>
          {roleLabel && (
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
              isExpert
                ? 'bg-emerald-500/15 text-emerald-400'
                : 'bg-blue-500/15 text-blue-400'
            }`}>
              {roleLabel}
            </span>
          )}
        </div>
        <div className="px-3 py-2">
          <p className="text-xs leading-relaxed text-foreground/90 max-h-40 overflow-y-auto">
            {content.length > 300 ? content.slice(0, 300) + '…' : content}
          </p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// Helper: parse text and replace [[cite:N]] with CitationBadge components
function renderTextWithCitations(
  text: string,
  citations: Record<string, string>,
): React.ReactNode[] {
  const parts = text.split(/(\[\[cite:\d+\]\])/g);
  return parts.map((part, i) => {
    const match = part.match(/^\[\[cite:(\d+)\]\]$/);
    if (match) {
      return <CitationBadge key={i} citeNumber={match[1]} citations={citations} />;
    }
    return part;
  });
}

// ── Custom Markdown renderers with citation support ───────────
// LLM generates image placeholders like ![Description]() — render as styled cards

function buildMarkdownComponents(citations: Record<string, string>): Components {
  return {
    img: ({ alt }) => (
      <div className="my-3 flex items-center gap-3 rounded-lg border border-dashed border-border/60 bg-muted/10 px-4 py-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
          <ImageIcon className="h-5 w-5 text-primary/70" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground/80 truncate">
            {alt || 'Visualisierung'}
          </p>
          <p className="text-[11px] text-muted-foreground">
            Diagramm / Visualisierung — wird bei Verfügbarkeit eingebettet
          </p>
        </div>
      </div>
    ),
    // Intercept paragraph text to inject citation badges
    p: ({ children }) => {
      const processed = processChildren(children, citations);
      return <p>{processed}</p>;
    },
    li: ({ children }) => {
      const processed = processChildren(children, citations);
      return <li>{processed}</li>;
    },
    td: ({ children }) => {
      const processed = processChildren(children, citations);
      return <td>{processed}</td>;
    },
    th: ({ children }) => {
      const processed = processChildren(children, citations);
      return <th>{processed}</th>;
    },
    strong: ({ children }) => {
      const processed = processChildren(children, citations);
      return <strong>{processed}</strong>;
    },
    em: ({ children }) => {
      const processed = processChildren(children, citations);
      return <em>{processed}</em>;
    },
    blockquote: ({ children }) => {
      return <blockquote>{children}</blockquote>;
    },
  };
}

// Process React children, splitting string children to inject citations
function processChildren(
  children: React.ReactNode,
  citations: Record<string, string>,
): React.ReactNode {
  if (children == null) return children;
  if (typeof children === 'string') {
    if (children.includes('[[cite:')) {
      return <>{renderTextWithCitations(children, citations)}</>;
    }
    return children;
  }
  if (Array.isArray(children)) {
    return children.map((child, i) => {
      if (typeof child === 'string' && child.includes('[[cite:')) {
        return <span key={i}>{renderTextWithCitations(child, citations)}</span>;
      }
      return child;
    });
  }
  return children;
}

// ── Collapsible section ───────────────────────────────────────

function CollapsibleSection({
  id,
  title,
  count,
  defaultOpen = true,
  icon: Icon,
  children,
}: {
  id?: string;
  title: string;
  count: number;
  defaultOpen?: boolean;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section id={id} className="scroll-mt-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 text-left group mb-3"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <Icon className="h-3.5 w-3.5 text-primary/70" />
        <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider group-hover:text-foreground transition-colors">
          {title}
        </h3>
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-muted/50 px-1.5 text-[10px] font-medium text-muted-foreground">
          {count}
        </span>
      </button>
      {open && children}
    </section>
  );
}

// ── Visual progress bar ───────────────────────────────────────

function ReviewProgressBar({
  approved,
  rejected,
  draft,
}: {
  approved: number;
  rejected: number;
  draft: number;
}) {
  const total = approved + rejected + draft;
  if (total === 0) return null;
  const pApprove = (approved / total) * 100;
  const pReject = (rejected / total) * 100;
  const pDraft = (draft / total) * 100;

  return (
    <div className="rounded-xl border border-border/50 bg-card/40 backdrop-blur-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-foreground">Review-Fortschritt</span>
        <span className="text-xs text-muted-foreground">
          {approved + rejected} / {total} entschieden
        </span>
      </div>

      {/* Bar */}
      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-muted/30">
        {pApprove > 0 && (
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
            style={{ width: `${pApprove}%` }}
          />
        )}
        {pReject > 0 && (
          <div
            className="absolute inset-y-0 rounded-full bg-gradient-to-r from-red-500 to-red-400 transition-all duration-500"
            style={{ left: `${pApprove}%`, width: `${pReject}%` }}
          />
        )}
        {pDraft > 0 && (
          <div
            className="absolute inset-y-0 rounded-full bg-gradient-to-r from-amber-500/40 to-amber-400/40 transition-all duration-500"
            style={{ left: `${pApprove + pReject}%`, width: `${pDraft}%` }}
          />
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[11px]">
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
          <span className="text-emerald-400 font-medium">{approved} freigegeben</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
          <span className="text-red-400 font-medium">{rejected} abgelehnt</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-amber-500/50" />
          <span className="text-amber-400 font-medium">{draft} offen</span>
        </div>
      </div>
    </div>
  );
}

// ── Report detail panel ───────────────────────────────────────

export function ReportDetailPanel({
  onFinalize,
  onMarkOutdated,
}: {
  onFinalize: () => void;
  onMarkOutdated: () => void;
}) {
  const { activeReport, detailLoading, approveEntity, rejectEntity } =
    useStewardStore();
  const { getAccessToken } = useApiToken();

  // DevPlan 0139/T2: Freigabe-/Governance-Aktionen erfordern WS-Reviewer
  // (Server: StewardController autoritativ; hier Defense-in-Depth).
  const { checkFeature } = useCanAccess();
  const canReviewEntities = checkFeature('steward', 'approve-entity');
  const canFinalize = checkFeature('steward', 'finalize-report');
  const canMarkOutdated = checkFeature('steward', 'mark-outdated');

  const handleApprove = useCallback(
    async (entityId: string) => {
      const token = await getAccessToken().catch(() => null);
      await approveEntity(entityId, token);
    },
    [getAccessToken, approveEntity],
  );

  const handleReject = useCallback(
    async (entityId: string) => {
      const token = await getAccessToken().catch(() => null);
      await rejectEntity(entityId, token);
    },
    [getAccessToken, rejectEntity],
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToSection = useCallback((sectionId: string) => {
    const container = scrollRef.current;
    if (!container) return;
    const el = container.querySelector(`#${sectionId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // ── Extract clean data from the report ──
  // Handles the case where summary is the full raw JSON blob (entities, citations etc. embedded).
  // Returns cleaned summary text, merged citations, and extracted entities/relationships.
  const cleanedReport = useMemo(() => {
    const apiCitations: Record<string, string> = { ...(activeReport?.citations ?? {}) };
    const apiEntities = activeReport?.entities ?? [];
    const apiRels = activeReport?.relationships ?? [];
    let summaryText = activeReport?.summary ?? '';
    let extractedEntities: DraftEntity[] = [];
    let extractedRelationships: DraftRelationship[] = [];

    // If summary looks like raw JSON, parse and extract fields
    const trimmed = summaryText.trimStart();
    if (trimmed.startsWith('{') || trimmed.startsWith('```')) {
      let jsonStr = summaryText;
      // Strip markdown code fences if present
      jsonStr = jsonStr.replace(/^```(?:json)?\s*\n?/i, '').replace(/\n?```\s*$/i, '');

      try {
        const parsed = JSON.parse(jsonStr);
        if (parsed && typeof parsed.summary === 'string') {
          summaryText = parsed.summary;

          // Merge citations from parsed JSON
          if (parsed.citations && typeof parsed.citations === 'object') {
            for (const [k, v] of Object.entries(parsed.citations)) {
              if (typeof v === 'string' && !apiCitations[k]) {
                apiCitations[k] = v;
              }
            }
          }

          // Extract entities if API returned none
          if (apiEntities.length === 0 && Array.isArray(parsed.entities)) {
            extractedEntities = parsed.entities.map((e: Record<string, unknown>, idx: number) => ({
              id: (e.id as string) ?? `extracted-${idx}`,
              name: (e.name as string) ?? '',
              type: (e.type as string) ?? 'Entity',
              description: (e.description as string) ?? '',
              status: 'DRAFT',
              ontologyClassification: (e.ontology_classification as string) ?? 'domain_extension',
              sortOrder: idx,
            }));
          }

          // Extract relationships if API returned none
          if (apiRels.length === 0 && Array.isArray(parsed.relationships)) {
            extractedRelationships = parsed.relationships.map((r: Record<string, unknown>, idx: number) => ({
              id: (r.id as string) ?? `extracted-rel-${idx}`,
              source: (r.source as string) ?? '',
              target: (r.target as string) ?? '',
              type: (r.type as string) ?? 'RELATED_TO',
              description: (r.description as string) ?? '',
              status: 'DRAFT',
              sortOrder: idx,
            }));
          }
        }
      } catch {
        // Not valid JSON — use as-is
      }
    }

    // Replace literal \n sequences with real newlines
    summaryText = summaryText.replace(/\\n/g, '\n');

    return {
      summary: summaryText,
      citations: apiCitations,
      entities: apiEntities.length > 0 ? apiEntities : extractedEntities,
      relationships: apiRels.length > 0 ? apiRels : extractedRelationships,
    };
  }, [activeReport]);

  const mdComponents = useMemo(
    () => buildMarkdownComponents(cleanedReport.citations),
    [cleanedReport.citations],
  );

  // Group entities by status — uses cleanedReport.entities (may include extracted-from-JSON entities)
  const grouped = useMemo(() => {
    const entities = cleanedReport.entities;
    return {
      draft: entities.filter((e) => e.status === 'DRAFT'),
      approved: entities.filter((e) => e.status === 'APPROVED'),
      rejected: entities.filter((e) => e.status === 'REJECTED'),
    };
  }, [cleanedReport.entities]);

  if (detailLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Report wird geladen…</span>
        </div>
      </div>
    );
  }

  if (!activeReport) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-5 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/10">
          <ShieldCheck className="h-10 w-10 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Wissenswart – Steward Review
          </h2>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
            Prüfe und genehmige Interview-Reports. Wähle einen Report links aus,
            um die vorgeschlagenen Entitäten und Beziehungen zu prüfen.
          </p>
        </div>
      </div>
    );
  }

  const report = activeReport;
  const { draft: draftEntities, approved: approvedEntities, rejected: rejectedEntities } = grouped;
  const allDecided = draftEntities.length === 0;

  return (
    <TooltipProvider>
    <div className="flex h-full flex-col">
      {/* ── Accent gradient bar ── */}
      <div className={`h-1 bg-gradient-to-r ${statusAccent[report.status] ?? 'from-primary/60 to-primary/30'}`} />

      {/* ── Report header ── */}
      <div className="border-b border-border/60 px-6 py-5 bg-card/30">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 border border-primary/20 shrink-0">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2.5">
                  <h2 className="text-base font-bold text-foreground truncate">
                    Interview-Report
                  </h2>
                  <StatusBadge status={report.status} />
                </div>
                <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <User className="h-3 w-3" />
                    {report.userName ?? '–'}
                  </span>
                  <span className="text-border">•</span>
                  <span className="inline-flex items-center gap-1.5">
                    <MessageSquare className="h-3 w-3" />
                    {report.messageCount} Nachrichten
                  </span>
                  <span className="text-border">•</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-3 w-3" />
                    {report.createdAt
                      ? new Date(report.createdAt).toLocaleString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '–'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {report.status === 'PENDING_REVIEW' && canMarkOutdated && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs text-amber-400 hover:text-amber-300 border-amber-500/30 hover:border-amber-500/50 hover:bg-amber-500/10 shrink-0"
              onClick={onMarkOutdated}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              Veraltet markieren
            </Button>
          )}
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className="border-b border-border/40 px-6 py-3 bg-muted/5">
        <div className="grid grid-cols-4 gap-3">
          <StatCard
            label="Entitäten"
            value={cleanedReport.entities.length}
            color="bg-blue-500/10 text-blue-400"
            icon={Boxes}
            onClick={() => scrollToSection('section-entities')}
          />
          <StatCard
            label="Beziehungen"
            value={cleanedReport.relationships.length}
            color="bg-purple-500/10 text-purple-400"
            icon={Network}
            onClick={() => scrollToSection('section-relationships')}
          />
          <StatCard
            label="Freigegeben"
            value={approvedEntities.length}
            color="bg-emerald-500/10 text-emerald-400"
            icon={CheckCircle2}
            onClick={() => scrollToSection('section-approved')}
          />
          <StatCard
            label="Offen"
            value={draftEntities.length}
            color="bg-amber-500/10 text-amber-400"
            icon={Sparkles}
            onClick={() => scrollToSection('section-draft')}
          />
        </div>
      </div>

      {/* ── Report body ── */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
        {/* Summary */}
        {cleanedReport.summary && (
          <section id="section-summary" className="scroll-mt-4 rounded-xl border border-border/40 bg-card/30 backdrop-blur-sm overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-border/30 bg-muted/10">
              <FileText className="h-3.5 w-3.5 text-primary/70" />
              <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                Zusammenfassung
              </h3>
            </div>
            <div className="px-5 py-4">
              <div className="prose prose-sm prose-invert max-w-none text-foreground/90
                [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mt-5 [&_h1]:mb-2.5 [&_h1]:text-foreground
                [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-foreground
                [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-foreground/90
                [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-3 [&_p]:text-foreground/80
                [&_ul]:text-sm [&_ul]:my-2 [&_ul]:pl-4 [&_ul]:text-foreground/80
                [&_ol]:text-sm [&_ol]:my-2 [&_ol]:pl-4 [&_ol]:text-foreground/80
                [&_li]:my-1 [&_li]:leading-relaxed
                [&_blockquote]:border-l-[3px] [&_blockquote]:border-primary/50 [&_blockquote]:pl-4 [&_blockquote]:py-1 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_blockquote]:bg-muted/10 [&_blockquote]:rounded-r-lg [&_blockquote]:my-3
                [&_table]:text-xs [&_table]:my-3 [&_table]:w-full [&_table]:border-collapse
                [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:border [&_th]:border-border/60 [&_th]:bg-muted/30
                [&_td]:px-3 [&_td]:py-2 [&_td]:border [&_td]:border-border/40
                [&_strong]:text-foreground [&_strong]:font-semibold
                [&_em]:text-foreground/70
                [&_code]:text-xs [&_code]:bg-muted/50 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded-md [&_code]:font-mono
                [&_pre]:rounded-lg [&_pre]:bg-muted/30 [&_pre]:border [&_pre]:border-border/40 [&_pre]:p-4 [&_pre]:my-3 [&_pre]:overflow-x-auto
                [&_img]:rounded-lg [&_img]:border [&_img]:border-border [&_img]:max-w-full [&_img]:my-3
                [&_hr]:border-border/30 [&_hr]:my-4
                [&_a]:text-primary [&_a]:underline [&_a]:decoration-primary/30 [&_a]:underline-offset-2 hover:[&_a]:decoration-primary/60
              ">
                <Markdown remarkPlugins={[remarkGfm]} components={mdComponents}>{cleanedReport.summary}</Markdown>
              </div>
            </div>
          </section>
        )}

        {/* Progress bar (PENDING_REVIEW only) */}
        {report.status === 'PENDING_REVIEW' && (
          <ReviewProgressBar
            approved={approvedEntities.length}
            rejected={rejectedEntities.length}
            draft={draftEntities.length}
          />
        )}

        {/* Entities grouped by status */}
        {draftEntities.length > 0 && (
          <CollapsibleSection id="section-draft" title="Offene Entitäten" count={draftEntities.length} icon={Sparkles}>
            <div className="space-y-2.5">
              {draftEntities.map((entity) => (
                <EntityCard
                  key={entity.id}
                  entity={entity}
                  citations={cleanedReport.citations}
                  onApprove={() => void handleApprove(entity.id)}
                  onReject={() => void handleReject(entity.id)}
                  canReview={canReviewEntities}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {approvedEntities.length > 0 && (
          <CollapsibleSection
            id="section-approved"
            title="Freigegebene Entitäten"
            count={approvedEntities.length}
            defaultOpen={draftEntities.length === 0}
            icon={CheckCircle2}
          >
            <div className="space-y-2.5">
              {approvedEntities.map((entity) => (
                <EntityCard
                  key={entity.id}
                  entity={entity}
                  citations={cleanedReport.citations}
                  onApprove={() => void handleApprove(entity.id)}
                  onReject={() => void handleReject(entity.id)}
                  canReview={canReviewEntities}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {rejectedEntities.length > 0 && (
          <CollapsibleSection
            title="Abgelehnte Entitäten"
            count={rejectedEntities.length}
            defaultOpen={false}
            icon={XCircle}
          >
            <div className="space-y-2.5">
              {rejectedEntities.map((entity) => (
                <EntityCard
                  key={entity.id}
                  entity={entity}
                  citations={cleanedReport.citations}
                  onApprove={() => void handleApprove(entity.id)}
                  onReject={() => void handleReject(entity.id)}
                  canReview={canReviewEntities}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Show all entities in a flat list for finalized/outdated reports */}
        {report.status !== 'PENDING_REVIEW' && cleanedReport.entities.length > 0 && (
          <CollapsibleSection id="section-entities" title="Entitäten" count={cleanedReport.entities.length} icon={Boxes}>
            <div className="space-y-2.5">
              {cleanedReport.entities.map((entity) => (
                <EntityCard
                  key={entity.id}
                  entity={entity}
                  citations={cleanedReport.citations}
                  onApprove={() => void handleApprove(entity.id)}
                  onReject={() => void handleReject(entity.id)}
                  canReview={canReviewEntities}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Relationships */}
        {cleanedReport.relationships.length > 0 && (
          <CollapsibleSection id="section-relationships" title="Beziehungen" count={cleanedReport.relationships.length} icon={Network}>
            <div className="space-y-2">
              {cleanedReport.relationships.map((rel) => (
                <RelationshipCard key={rel.id} rel={rel} />
              ))}
            </div>
          </CollapsibleSection>
        )}
      </div>

      {/* ── Footer: Finalize CTA ── */}
      {report.status === 'PENDING_REVIEW' && (
        <div className="border-t border-border/60 px-6 py-4 bg-card/30">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                {allDecided
                  ? `${approvedEntities.length} Entität(en) werden übernommen`
                  : `${draftEntities.length} Entität(en) noch offen`}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {/* DevPlan 0139/T2: Finalisierung nur für WS-Reviewer. */}
                {!canFinalize
                  ? 'Finalisierung erfordert die Workspace-Rolle „Reviewer".'
                  : allDecided
                    ? 'Alle Entitäten wurden geprüft. Bereit zur Finalisierung.'
                    : 'Offene Entitäten werden beim Finalisieren automatisch abgelehnt.'}
              </p>
            </div>
            {canFinalize && (
              <Button
                className="gap-2 bg-emerald-600 hover:bg-emerald-500 text-white shrink-0"
                onClick={onFinalize}
                disabled={approvedEntities.length === 0}
              >
                <CheckCircle2 className="h-4 w-4" />
                Report finalisieren
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}
